import React, { useState } from "react";
import {
  FaLock,
  FaCalendarAlt,
  FaClipboardList,
  FaUser,
  FaShoppingBag,
  FaLaptop,
  FaCog,
  FaHandshake,
  FaShieldAlt,
  FaCookieBite,
  FaCheckCircle,
  FaChild,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaEnvelope,
  FaRegClock,
  FaExclamationTriangle,
  FaDatabase,
  FaChartLine,
  FaBox,
  FaGavel
} from "react-icons/fa";
import "../styles/PrivacyPolicy.css";

const PrivacyPolicy = () => {
  const [lastUpdated] = useState("03 May 2026");

  return (
    <div className="policy-container">
      {/* Header */}
      <div className="policy-header">
        <div className="header-badge">
          <span className="logo-icon">
            <FaLock />
          </span>
          <h1>Privacy Policy</h1>
        </div>
        <p className="last-updated">Last Updated: {lastUpdated}</p>
        <p className="tagline">
          KKB — Tradition through Generations | Lungies • Towels • Bedsheets
        </p>
      </div>

      <div className="policy-content">
        {/* Overview */}
        <section className="policy-section">
          <h2>
            <FaClipboardList style={{ marginRight: "8px" }} /> Overview
          </h2>
          <p>
            At KKB ("KKB Traditions", "we", "us", "our"), we value your privacy
            and are committed to protecting your personal information. This
            Privacy Policy explains how we collect, use, disclose, and safeguard
            your information when you visit our website or make purchases of our
            traditional Lungies, Towels, Bedsheets, and other home textile
            products.
          </p>
          <p>
            Please read this privacy policy carefully. By accessing or using our
            services, you acknowledge that you have read, understood, and agree
            to be bound by all the terms of this Privacy Policy.
          </p>
        </section>

        {/* Information Collection */}
        <section className="policy-section">
          <h2>
            <FaDatabase style={{ marginRight: "8px" }} /> Information We Collect
          </h2>
          <div className="info-grid">
            <div className="info-card">
              <span className="info-icon">
                <FaUser />
              </span>
              <h3>Personal Information</h3>
              <ul>
                <li>Name, email address, phone number</li>
                <li>Shipping and billing addresses</li>
                <li>Date of birth (for age verification)</li>
                <li>Payment information (cards, UPI, net banking)</li>
              </ul>
            </div>
            <div className="info-card">
              <span className="info-icon">
                <FaShoppingBag />
              </span>
              <h3>Order Information</h3>
              <ul>
                <li>Products purchased (Lungies, Towels, Bedsheets)</li>
                <li>Order history and preferences</li>
                <li>Size preferences (Lungi sizes, bedsheet dimensions)</li>
                <li>Return and cancellation history</li>
              </ul>
            </div>
            <div className="info-card">
              <span className="info-icon">
                <FaLaptop />
              </span>
              <h3>Technical Information</h3>
              <ul>
                <li>IP address and device information</li>
                <li>Browser type and version</li>
                <li>Pages visited and time spent</li>
                <li>Cookies and tracking technologies</li>
              </ul>
            </div>
          </div>
        </section>

        {/* How We Use Information */}
        <section className="policy-section">
          <h2>
            <FaCog style={{ marginRight: "8px" }} /> How We Use Your Information
          </h2>
          <div className="uses-list">
            <div className="use-item">
              <span className="use-number">01</span>
              <div>
                <strong>Order Processing</strong>
                <p>
                  Process and fulfill your orders for Lungies, Towels,
                  Bedsheets, and manage deliveries
                </p>
              </div>
            </div>
            <div className="use-item">
              <span className="use-number">02</span>
              <div>
                <strong>Customer Support</strong>
                <p>
                  Respond to your queries, process returns, exchanges, and
                  handle complaints
                </p>
              </div>
            </div>
            <div className="use-item">
              <span className="use-number">03</span>
              <div>
                <strong>Personalization</strong>
                <p>
                  Recommend products based on your preferences and purchase
                  history
                </p>
              </div>
            </div>
            <div className="use-item">
              <span className="use-number">04</span>
              <div>
                <strong>Communication</strong>
                <p>
                  Send order updates, promotional offers, and important notices
                </p>
              </div>
            </div>
            <div className="use-item">
              <span className="use-number">05</span>
              <div>
                <strong>Security & Fraud Prevention</strong>
                <p>
                  Protect against unauthorized transactions and ensure safe
                  shopping
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Information Sharing */}
        <section className="policy-section">
          <h2>
            <FaHandshake style={{ marginRight: "8px" }} /> Information Sharing &
            Disclosure
          </h2>
          <p>
            We do not sell, trade, or rent your personal information to third
            parties. However, we may share your information with:
          </p>
          <div className="sharing-grid">
            <div className="sharing-card">
              <strong>
                <FaBox style={{ marginRight: "6px" }} /> Delivery Partners
              </strong>
              <p>To ship your Lungies, Towels, and Bedsheets to your address</p>
            </div>
            <div className="sharing-card">
              <strong>
                <FaChartLine style={{ marginRight: "6px" }} /> Payment
                Processors
              </strong>
              <p>To securely process payments and prevent fraud</p>
            </div>
            <div className="sharing-card">
              <strong>
                <FaEnvelope style={{ marginRight: "6px" }} /> Service Providers
              </strong>
              <p>
                Email services, SMS notifications, and customer support tools
              </p>
            </div>
            <div className="sharing-card">
              <strong>
                <FaGavel style={{ marginRight: "6px" }} /> Legal Authorities
              </strong>
              <p>When required by law or to protect our rights</p>
            </div>
          </div>
        </section>

        {/* Data Security */}
        <section className="policy-section">
          <h2>
            <FaShieldAlt style={{ marginRight: "8px" }} /> Data Security
          </h2>
          <p>
            We implement industry-standard security measures to protect your
            personal information:
          </p>
          <ul className="security-list">
            <li>
              <FaLock style={{ marginRight: "8px" }} />{" "}
              <strong>SSL Encryption</strong> — All data transmitted is
              encrypted
            </li>
            <li>
              <FaDatabase style={{ marginRight: "8px" }} />{" "}
              <strong>Secure Servers</strong> — Protected with firewalls and
              intrusion detection
            </li>
            <li>
              <FaUser style={{ marginRight: "8px" }} />{" "}
              <strong>Access Control</strong> — Limited employee access to
              customer data
            </li>
            <li>
              <FaClipboardList style={{ marginRight: "8px" }} />{" "}
              <strong>Regular Audits</strong> — Security assessments and
              vulnerability testing
            </li>
          </ul>
          <div className="security-note">
            <p>
              <FaExclamationTriangle style={{ marginRight: "8px" }} />{" "}
              <strong>Note:</strong> While we strive to protect your data, no
              method of transmission over the Internet is 100% secure.
            </p>
          </div>
        </section>

        {/* Cookies */}
        <section className="policy-section">
          <h2>
            <FaCookieBite style={{ marginRight: "8px" }} /> Cookies & Tracking
            Technologies
          </h2>
          <p>
            We use cookies to enhance your shopping experience. Cookies help us:
          </p>
          <ul className="cookies-list">
            <li>Remember your login and preferences</li>
            <li>Understand how you navigate our website</li>
            <li>Show personalized product recommendations</li>
            <li>Analyze website traffic and performance</li>
          </ul>
          <p>
            You can control cookie settings through your browser preferences.
          </p>
        </section>

        {/* Your Rights */}
        <section className="policy-section highlight-section">
          <h2>
            <FaCheckCircle style={{ marginRight: "8px" }} /> Your Rights &
            Choices
          </h2>
          <p>
            You have the following rights regarding your personal information:
          </p>
          <div className="rights-grid">
            <div className="right-item">
              <span className="right-check">✓</span>
              <span>Access your personal data</span>
            </div>
            <div className="right-item">
              <span className="right-check">✓</span>
              <span>Correct inaccurate information</span>
            </div>
            <div className="right-item">
              <span className="right-check">✓</span>
              <span>Request deletion of your data</span>
            </div>
            <div className="right-item">
              <span className="right-check">✓</span>
              <span>Opt-out of marketing communications</span>
            </div>
            <div className="right-item">
              <span className="right-check">✓</span>
              <span>Download your data in portable format</span>
            </div>
          </div>
          <p className="contact-cta">
            To exercise any of these rights, contact us at{" "}
            <strong>privacy@kkb.com</strong>
          </p>
        </section>

        {/* Children's Privacy */}
        <section className="policy-section">
          <h2>
            <FaChild style={{ marginRight: "8px" }} /> Children's Privacy
          </h2>
          <p>
            Our services are not intended for individuals under 18 years of age.
            We do not knowingly collect personal information from children. If
            you believe we have collected information from a minor, please
            contact us immediately.
          </p>
        </section>

        {/* Policy Updates */}
        <section className="policy-section">
          <h2>
            <FaCalendarAlt style={{ marginRight: "8px" }} /> Changes to This
            Privacy Policy
          </h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify
            you of any changes by posting the new policy on this page and
            updating the "Last Updated" date. We encourage you to review this
            policy periodically.
          </p>
        </section>

        {/* Contact */}
        <section className="policy-section contact-section">
          <h2>
            <FaPhoneAlt style={{ marginRight: "8px" }} /> Contact Us
          </h2>
          <p>
            If you have questions about this Privacy Policy or our data
            practices, please contact us:
          </p>
          <div className="contact-details">
            <p>
              <FaMapMarkerAlt style={{ marginRight: "8px" }} />{" "}
              <strong>KKB Traditions Pvt Ltd</strong>
            </p>
            <p>No. 42, Heritage Complex, RS Puram</p>
            <p>Coimbatore - 641018, Tamil Nadu, India</p>
            <p>
              <FaEnvelope style={{ marginRight: "8px" }} /> Email:
              privacy@kkb.com
            </p>
            <p>
              <FaPhoneAlt style={{ marginRight: "8px" }} /> Phone:
              +91-422-1234567
            </p>
            <p>
              <FaRegClock style={{ marginRight: "8px" }} /> Support Hours: 9 AM
              - 9 PM, All Days
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
